import React, { useCallback, useMemo, useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator } from 'react-native';
// `Alert` daqui, nao do react-native: no navegador o Alert do RN e um stub
// vazio e engole tudo em silencio (ver src/utils/appAlert.js). Em nativo e
// reexportacao direta - comportamento do app inalterado.
import { Alert } from '../../../utils/appAlert';
import * as Haptics from 'expo-haptics';
import { Text } from '../../../components/ui/AppText';
import { useUserPosts, useDeletePost } from '../../../hooks/queries/useCommunityQuery';
import {
  useUserVideos,
  useDeleteVideo,
  selectVideoItems,
  VideoPlayerModal,
} from '../../communityVideos';
import ProfilePostCard from './ProfilePostCard';
import ProfileVideoCard from './ProfileVideoCard';
import { styles } from './styles';

const SUB = Object.freeze({ POSTS: 'posts', VIDEOS: 'videos' });

/**
 * Lista de conteúdo de um usuário (Posts | Vídeos) para a tela de perfil.
 * - `isOwner` controla SOMENTE a UI de apagar (autorização real = RLS no Postgres).
 * - Compõe dentro do ScrollView da tela (usa .map() + "Carregar mais", sem
 *   FlashList aninhada → evita conflito de scroll/virtualização).
 * - Zero acesso a Supabase: consome apenas hooks dos domínios (baixo acoplamento).
 */
const UserContentList = ({ userId, isOwner = false, colors, enabled = true }) => {
  const [sub, setSub] = useState(SUB.POSTS);
  const [playingIndex, setPlayingIndex] = useState(null);

  const postsQuery = useUserPosts(userId, enabled);
  const videosQuery = useUserVideos(userId, enabled);
  const deletePost = useDeletePost();
  const deleteVideo = useDeleteVideo();

  const posts = useMemo(
    () => postsQuery.data?.pages?.flatMap((page) => page.posts ?? []) ?? [],
    [postsQuery.data]
  );
  const videos = useMemo(() => selectVideoItems(videosQuery.data), [videosQuery.data]);

  const confirmDelete = useCallback((label, onConfirm) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
    Alert.alert(
      'Apagar',
      `Tem certeza que deseja apagar ${label}? Esta ação não pode ser desfeita.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Apagar', style: 'destructive', onPress: onConfirm },
      ]
    );
  }, []);

  const handleDeletePost = useCallback((post) => {
    confirmDelete('este post', () => {
      deletePost.mutate(post.id, {
        onError: (error) => Alert.alert('Erro', error?.message || 'Não foi possível apagar o post.'),
      });
    });
  }, [confirmDelete, deletePost]);

  const handleDeleteVideo = useCallback((video) => {
    confirmDelete('este vídeo', () => {
      deleteVideo.mutate(video, {
        onError: (error) => Alert.alert('Erro', error?.message || 'Não foi possível apagar o vídeo.'),
      });
    });
  }, [confirmDelete, deleteVideo]);

  const handleVideoPress = useCallback((video) => {
    const idx = videos.findIndex((v) => v.id === video.id);
    setPlayingIndex(idx >= 0 ? idx : 0);
  }, [videos]);

  const handleVideoEndReached = useCallback(() => {
    if (videosQuery.hasNextPage && !videosQuery.isFetchingNextPage) videosQuery.fetchNextPage();
  }, [videosQuery]);

  const isPosts = sub === SUB.POSTS;
  const activeQuery = isPosts ? postsQuery : videosQuery;
  const items = isPosts ? posts : videos;
  const accent = colors?.accentPrimary || '#5B8FF9';

  return (
    <View style={styles.wrap}>
      <View style={styles.segment}>
        {[SUB.POSTS, SUB.VIDEOS].map((key) => {
          const active = sub === key;
          return (
            <TouchableOpacity
              key={key}
              style={[styles.segmentBtn, active && styles.segmentBtnActive]}
              onPress={() => setSub(key)}
              activeOpacity={0.8}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
            >
              <Text style={[styles.segmentText, { color: active ? '#FFFFFF' : (colors?.textSecondary || '#A0A3B1') }]}>
                {key === SUB.POSTS ? 'Posts' : 'Vídeos'}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {activeQuery.isLoading ? (
        <ActivityIndicator style={styles.loader} color={accent} />
      ) : items.length === 0 ? (
        <Text style={[styles.empty, { color: colors?.textSecondary || '#A0A3B1' }]}>
          {isOwner
            ? (isPosts ? 'Você ainda não publicou posts.' : 'Você ainda não publicou vídeos.')
            : 'Nenhum conteúdo publicado.'}
        </Text>
      ) : isPosts ? (
        posts.map((post) => (
          <ProfilePostCard
            key={post.id}
            post={post}
            colors={colors}
            isOwner={isOwner}
            onDelete={handleDeletePost}
          />
        ))
      ) : (
        videos.map((video) => (
          <ProfileVideoCard
            key={video.id}
            video={video}
            colors={colors}
            isOwner={isOwner}
            onDelete={handleDeleteVideo}
            onPress={handleVideoPress}
          />
        ))
      )}

      {!activeQuery.isLoading && activeQuery.hasNextPage && (
        <TouchableOpacity
          style={styles.loadMore}
          onPress={() => activeQuery.fetchNextPage()}
          disabled={activeQuery.isFetchingNextPage}
        >
          {activeQuery.isFetchingNextPage ? (
            <ActivityIndicator size="small" color={accent} />
          ) : (
            <Text style={[styles.loadMoreText, { color: accent }]}>Carregar mais</Text>
          )}
        </TouchableOpacity>
      )}

      <VideoPlayerModal
        videos={videos}
        initialIndex={playingIndex ?? 0}
        visible={playingIndex != null}
        onClose={() => setPlayingIndex(null)}
        onEndReached={handleVideoEndReached}
      />
    </View>
  );
};

export default React.memo(UserContentList);
